import { useNavigate } from "react-router-dom";
import { useTheme } from "../../theme/useTheme";
import { THEMES, ThemeId } from "../../theme/themes";
import { LayoutAdminCafe } from "../../layout/layout_cafe";

const ThemePickerInner: React.FC = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();

  return (
    <div className="min-h-screen bg-white font-sans pb-[64px]">
      {/* Header */}
      <header className="px-5 pt-[24px] pb-[20px] flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-ink-700 hover:bg-ink-100 transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1 className="font-display font-black text-[22px] tracking-tightest text-ink-1000">
          Theme
        </h1>
      </header>
      <div className="border-b border-ink-100" />

      {/* Intro */}
      <div className="px-5 pt-[24px] pb-[16px]">
        <p className="eyebrow-v2 mb-2">Festive mode</p>
        <h2 className="font-display font-black text-[24px] leading-tight tracking-tightest text-ink-1000">
          Match the cafe's vibe
        </h2>
        <p className="text-sm text-ink-500 mt-2">
          Pick a look. Everyone signed in will see it instantly.
        </p>
      </div>

      {/* Grid */}
      <div className="px-5 grid grid-cols-2 gap-3 pt-[16px]">
        {THEMES.map((t) => {
          const active = t.id === theme;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTheme(t.id as ThemeId)}
              className={`relative text-left rounded-3xl border p-4 transition-all active:scale-[0.98] ${
                active
                  ? "border-ink-1000 bg-ink-50"
                  : "border-ink-100 hover:border-ink-300 bg-white"
              }`}
            >
              {/* Swatch preview */}
              <div className="relative h-[100px] rounded-2xl overflow-hidden mb-3 flex">
                <div className="flex-1" style={{ background: t.swatches[0] }} />
                <div className="flex-1" style={{ background: t.swatches[1] }} />
                <div className="absolute inset-0 flex items-center justify-center text-3xl">
                  {t.emoji}
                </div>
              </div>
              {/* Label */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-display font-bold text-base text-ink-1000">{t.name}</p>
                  <p className="text-xs text-ink-500 mt-0.5">{t.description}</p>
                </div>
                {active && (
                  <div className="flex-shrink-0 w-[24px] h-[24px] rounded-full bg-ink-1000 text-white flex items-center justify-center">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Live preview — multiple surfaces so user can see exactly what changes */}
      <div className="px-5 pt-[32px] space-y-4">
        <p className="eyebrow-v2">Live preview</p>

        {/* Hero surface */}
        <div className="rounded-3xl bg-hero text-hero-on p-6 relative overflow-hidden">
          <div className="absolute -right-4 -top-4 w-[120px] h-[120px] rounded-full" style={{ background: "var(--festive-1)", opacity: "var(--decor-opacity)" }} />
          <div className="absolute -right-8 top-12 w-[90px] h-[90px] rounded-full" style={{ background: "var(--festive-2)", opacity: "var(--decor-opacity)" }} />
          <div className="relative">
            <p className="eyebrow-v2 mb-2" style={{ color: "var(--hero-muted)" }}>
              {THEMES.find((t) => t.id === theme)?.name}
            </p>
            <h3 className="font-display font-black text-[24px] tracking-tightest">
              A great day for coffee.
            </h3>
            <button
              type="button"
              className="mt-4 inline-flex items-center gap-2 h-[40px] px-5 rounded-full font-semibold text-sm bg-white"
              style={{ color: "var(--accent)" }}
            >
              Order now
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Accent surface samples */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-2xl p-3 text-center bg-accent text-accent-on">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-80">Accent</p>
            <p className="font-display font-black text-[24px] mt-0.5">Aa</p>
          </div>
          <div className="rounded-2xl p-3 text-center" style={{ background: "var(--festive-1)", color: "white" }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-80">Festive 1</p>
            <p className="font-display font-black text-[24px] mt-0.5">Aa</p>
          </div>
          <div className="rounded-2xl p-3 text-center" style={{ background: "var(--festive-2)", color: "var(--hero-bg)" }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-80">Festive 2</p>
            <p className="font-display font-black text-[24px] mt-0.5">Aa</p>
          </div>
        </div>

        {/* Buttons sample */}
        <div className="flex gap-2 flex-wrap">
          <button type="button" className="btn-v2-primary btn-v2-md">Primary</button>
          <button type="button" className="btn-v2-secondary btn-v2-md">Secondary</button>
          <span className="chip-v2-active">Active chip</span>
          <span className="chip-v2">Chip</span>
        </div>

        <p className="text-xs text-ink-500 text-center pt-2">
          Theme is saved to this browser. Open another page to see it applied throughout.
        </p>
      </div>
    </div>
  );
};

const ThemePicker: React.FC = () => (
  <LayoutAdminCafe>
    <ThemePickerInner />
  </LayoutAdminCafe>
);

export default ThemePicker;
